import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { PMT2Product } from '@/types/pmt2Types';
import { db } from '@/config/firebase';
import { collection, getDocs } from 'firebase/firestore';
import { Timestamp } from 'firebase/firestore';
 
import { X } from 'lucide-react';
import { ExternalLink } from 'lucide-react';
import KeyboardNavigableSelect from '@/components/ui/KeyboardNavigableSelect';

interface BundleLedgerViewerDashboardProps {
  product: PMT2Product | null;
}

interface BundleLedgerEntry {
  id: string;
  date: Timestamp;
  yourName: string;
  dataElement: string;
  newValue: string;
  note: string;
  [key: string]: any;
}

/**
 * Bundle Ledger Viewer Dashboard Component
 * 
 * Displays a table of bundle ledger entries for a selected product
 * Shows Date, Your Name, Data Element, New Value, and Note
 * Allows filtering by Data Element and sorting by Date
 */
const BundleLedgerViewerDashboard: React.FC<BundleLedgerViewerDashboardProps> = ({ product }) => {
  // State variables
  const [entries, setEntries] = useState<BundleLedgerEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [dataElementFilter, setDataElementFilter] = useState<string>('all');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [uniqueDataElements, setUniqueDataElements] = useState<string[]>([]);

  // List of Data Elements to exclude from the dropdown
  const excludedDataElements = [
    'Listing Brand Name',
    'Standard ASIN Review',
    'Manufacturer',
    'Model',
    'Bullet Point 1',
    'Bullet Point 2',
    'Bullet Point 3',
    'Bullet Point 4',
    'Bullet Point 5',
    'Description',
    'A+ Content',
    'A+ Images',
    'Image URL 1',
    'Image URL 2',
    'Image URL 3',
    'Image URL 4',
    'Image URL 5',
    'Image URL 6',
    'Image URL 7',
    'Image URL 8'
  ];

  // Fetch ledger entries
  const fetchLedgerEntries = useCallback(async () => {
    if (!product || !product.companyName || !product.id) {
      setEntries([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Get all data element documents for the selected ASIN
      const dataElementsRef = collection(db, 'pmt2_ledger', product.companyName, product.id);
      const dataElementsSnapshot = await getDocs(dataElementsRef);

      // For each data element, get the entries
      let allEntries: BundleLedgerEntry[] = [];
      const dataElements = new Set<string>();

      for (const dataElementDoc of dataElementsSnapshot.docs) {
        const dataElementData = dataElementDoc.data();
        const originalDataElement = dataElementData.originalDataElement || dataElementDoc.id;
        
        // Add to unique data elements
        dataElements.add(originalDataElement);
        
        if (dataElementData.entries && Array.isArray(dataElementData.entries)) {
          // Map entries to ensure they have the correct structure
          const mappedEntries = dataElementData.entries.map((entry: any) => ({
            id: `${originalDataElement}_${entry.id || new Date().getTime()}_${Math.random().toString(36).substring(2, 9)}`,
            date: entry.date || Timestamp.now(),
            yourName: entry['Your Name'] || entry.yourName || entry['your name'] || '',
            dataElement: originalDataElement, // Always use the originalDataElement from the document
            newValue: entry['New Value'] || entry.newValue || entry['new value'] || '',
            note: entry.Note || entry.note || entry['note'] || '',
            ...entry // Include all original fields
          }));
          
          allEntries = [...allEntries, ...mappedEntries];
        }
      }

      // Set state
      setEntries(allEntries);
      // Filter out excluded Data Elements
      const filteredDataElements = Array.from(dataElements)
        .filter(element => !excludedDataElements.includes(element))
        .sort();
      setUniqueDataElements(filteredDataElements);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching ledger entries:', error instanceof Error ? error.message : error);
      setError('Failed to load ledger entries. Please try again later.');
      setLoading(false);
    }
  }, [product]);

  // Filter and sort entries
  const filteredAndSortedEntries = useMemo(() => {
    // Filter by Data Element if a filter is selected
    // First, filter out entries with excluded Data Elements (case-insensitive)
    let filtered = entries.filter(entry => 
      !excludedDataElements.some(excluded => 
        excluded.toLowerCase() === entry.dataElement.toLowerCase()
      )
    );
    
    // Then apply the Data Element filter if selected
    if (dataElementFilter && dataElementFilter !== 'all') {
      filtered = filtered.filter(entry => 
        entry.dataElement.toLowerCase() === dataElementFilter.toLowerCase()
      );
    }
    
    // Sort by date
    return [...filtered].sort((a, b) => {
      const dateA = a.date?.toDate?.() || new Date();
      const dateB = b.date?.toDate?.() || new Date();
      return sortOrder === 'asc' 
        ? dateA.getTime() - dateB.getTime() 
        : dateB.getTime() - dateA.getTime();
    });
  }, [entries, dataElementFilter, sortOrder, excludedDataElements]);

  // Function to detect URLs in text and make them clickable
  const renderWithClickableLinks = (text: string): React.ReactNode => {
    if (!text || text === '-') return '-';

    // URL regex pattern
    const urlPattern = /(https?:\/\/[^\s]+)/g;

    // Check if the text is just a URL
    if (text.match(urlPattern)?.[0] === text) {
      return (
        <a 
          href={text} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline inline-flex items-center"
        >
          Link
          <ExternalLink className="h-3 w-3 ml-1" />
        </a>
      );
    }

    // If it's not just a URL, check if it contains URLs
    const matches = text.match(urlPattern);
    if (!matches) return text;

    // Replace each URL with a "Link" text
    let result = text;
    matches.forEach((url) => {
      result = result.replace(url, `[Link](${url})`);
    });
  
    // Convert markdown-style links to actual links
    const markdownLinkPattern = /\[Link\]\((https?:\/\/[^\s)]+)\)/g;
    const parts = result.split(markdownLinkPattern);
    const markdownMatches = result.match(markdownLinkPattern) || [];

    if (parts.length <= 1) return result;

    return (
      <>
        {parts.map((part, index) => (
          <React.Fragment key={index}>
            {part}
            {markdownMatches[index] && (() => {
              const url = markdownMatches[index].match(/\(([^)]+)\)/)?.[1] || '';
              return (
                <a 
                  href={url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline inline-flex items-center"
                >
                  Link
                  <ExternalLink className="h-3 w-3 ml-1" />
                </a>
              );
            })()}
          </React.Fragment>
        ))}
      </>
    );
  };

  // Effect to fetch data when product changes
  useEffect(() => {
    if (product) {
      try {
        // Reset filters when product changes
        setDataElementFilter('all');
        fetchLedgerEntries();
      } catch (error) {
        console.error('Error in useEffect:', error instanceof Error ? error.message : error);
      }
    } else {
      setEntries([]);
      setUniqueDataElements([]);
    }
  }, [product]);

  // Format date to "MM/DD/YY" format
  const formatDate = (timestamp: Timestamp): string => {
    if (!timestamp || !timestamp.toDate) return '';
    
    try {
      const date = timestamp.toDate();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const year = date.getFullYear().toString().slice(-2);
      return `${month}/${day}/${year}`;
    } catch (error) {
      return '';
    }
  };

  // Render UI
  if (!product) return null;

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
        <h2 className="text-2xl font-bold mb-6">Bundle Ledger Viewer</h2>
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
          <span className="ml-2">Loading ledger entries...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
        <h2 className="text-2xl font-bold mb-6">Bundle Ledger Viewer</h2>
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 p-4 rounded-md text-red-700 dark:text-red-300">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm">
      <h2 className="text-2xl font-bold mb-6">Bundle Ledger Viewer</h2>
      
      {/* Filters */}
      <div className="flex justify-between mb-6 gap-4">
        <div className="w-1/3">
          <label className="block text-sm font-medium mb-1">Data Element</label>
          <div className="relative">
            <KeyboardNavigableSelect
              options={[
                { value: "all", label: "All" },
                ...uniqueDataElements.map(element => ({ value: element, label: element }))
              ]}
              value={dataElementFilter}
              onValueChange={setDataElementFilter}
              placeholder="Select Data Element"
              className="w-full"
              triggerClassName="w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              triggerStyle={{ minHeight: '36px' }}
              contentStyle={{ backgroundColor: 'white', color: 'black', zIndex: 50 }}
              itemStyle={{ color: 'black' }}
            />
            {dataElementFilter !== 'all' && (
              <button
                onClick={() => setDataElementFilter('all')}
                className="absolute right-8 top-1/2 transform -translate-y-1/2 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none"
                aria-label="Clear filter"
                title="Clear filter"
              >
                <X className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              </button>
            )}
          </div>
        </div>
        <div className="w-1/3">
          <label className="block text-sm font-medium mb-1">Sort</label>
          <KeyboardNavigableSelect
            options={[
              { value: "desc", label: "Date Descending" },
              { value: "asc", label: "Date Ascending" }
            ]}
            value={sortOrder}
            onValueChange={(value) => setSortOrder(value as 'asc' | 'desc')}
            placeholder="Select Sort Order"
            className="w-full"
            triggerClassName="w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            triggerStyle={{ minHeight: '36px' }}
            contentStyle={{ backgroundColor: 'white', color: 'black', zIndex: 50 }}
            itemStyle={{ color: 'black' }}
          />
        </div>
      </div>
      
      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Date
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Your Name
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Data Element
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                New Value
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                Note
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {filteredAndSortedEntries.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                  No ledger entries found for this ASIN.
                </td>
              </tr>
            ) : (
              filteredAndSortedEntries.map((entry, index) => (
                <tr key={`${entry.id}_${index}`} className={index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700'}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {formatDate(entry.date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {entry.yourName || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                    {entry.dataElement || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100 break-words max-w-md">
                    {renderWithClickableLinks(entry.newValue)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                    {entry.note || '-'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BundleLedgerViewerDashboard;